import React, { useMemo } from 'react';
import { useUnistyles } from 'react-native-unistyles';
import { Text, View, Pressable} from 'react-native';
import Avatar from '@/components/People/Avatar';
import { ActivityIndicator } from 'react-native';
import usePlatform from '@/utils/hooks/usePlatform';
import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '../General/Unicon';


const ProfileCard = props => {
    const { profile, subtitle = "", secondarySubtitle = null, secondarySubtitleIcon = null, onPress = null, onLongPress = null, selected = null, selectedColorVariant = null, selectedIcon = "checkmark-circle", processing = false, minimal = false, sameHousehold = false, backgroundColorVariant = null, buttons = null, testID = null } = props;
    const {theme: {colors}} = useUnistyles();
    const { isLandscape } = usePlatform();
    const router = useRouter();

    const selectedColorDefault = useMemo(() => selectedColorVariant != null ? selectedColorVariant : colors.success, [selectedColorVariant])

    return (!profile ? null :
        <View>
            <Pressable
                testID={testID || `ProfileCard-${profile?.firstName} ${profile?.lastName}`}
                hitSlop={5}
                onLongPress={() => {
                    if(!onLongPress){
                        router.push('/people/' + profile?.id)
                    }
                    else{
                        if(!processing){
                            onLongPress(profile);
                        }
                    }
                }} 
                onPress={() => {
                    if(!onPress && !buttons){
                        router.push('/people/' + profile?.id)
                    }
                    else if(onPress && !processing){
                        onPress(profile);
                    }
                }} 
                style={styles.card(!!buttons, minimal, backgroundColorVariant, selected, selectedColorDefault)}
            >
                 <View style={styles.content}>
                    <Avatar borderGap disableBorder size={minimal ? 32 : 42} profile={profile} />

                    <View style={styles.nameContainer}>
                        <View style={styles.content}>
                            <Text style={styles.header(profile?.isArchived)}>{profile?.firstName + " " + profile?.lastName} </Text>
                            {sameHousehold && <Unicon name="home-outline" size={12} colorVariant="textSecondary" />}
                            {profile?.isArchived && <View style={styles.archivedBadge}><Text style={styles.archivedBadgeText}>Archived</Text></View>}
                        </View>
                        {(!minimal || (minimal && !!subtitle)) && <Text numberOfLines={2} style={styles.sub_header(minimal)}>{subtitle || profile?.email}</Text>}
                        {secondarySubtitle && (
                            <View style={styles.secondarySubtitle}>
                                {secondarySubtitleIcon && <Unicon name={secondarySubtitleIcon} size={13} colorVariant="success" />}
                                <Text style={styles.secondarySubtitleText}>{secondarySubtitle}</Text>
                            </View>
                        )}
                    </View>
                </View>

                <View style={{flexDirection: "row", alignItems: "center", gap: 8}}>
                    {processing
                        ? <ActivityIndicator />
                        :
                        selected !== null
                            ? selected ? <Unicon testID={selectedIcon} name={selectedIcon} size={28} colorVariant={selectedColorDefault}/> : <Unicon name="ellipse-outline" size={28} colorVariant="textSecondary"/>
                            : buttons ? <View style={styles.buttonContainer(isLandscape)}>{buttons}</View> : <Unicon name="chevron-forward-outline" size={25} colorVariant="text"/>
                    }
                </View>
            </Pressable>
        </View>
    )
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    header: (isArchived) => ({
        fontSize:14,
        textAlign:"left",
        fontFamily: fonts.heading,
        color: isArchived ? colors.textTertiary : colors.text
    }),
    archivedBadge: {
        backgroundColor: colors.warningSubtle,
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 4,
        marginLeft: 6,
    },
    archivedBadgeText: {
        fontSize: 10,
        fontFamily: fonts.heading,
        color: colors.warning,
        textTransform: "uppercase",
    },
    sub_header: (minimal) => ({
        marginTop: minimal ? 0 : 5,
        fontSize:12,
        fontStyle:"italic",
        textAlign:"left",
        fontFamily: fonts.body,
        color: colors.textSecondary
    }),
    secondarySubtitle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: 3,
    },
    secondarySubtitleText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.success,
    },
    card: (buttons, minimal, backgroundColorVariant, selected, selectedColorDefault) => ({
        flexWrap:buttons ? "wrap" : "nowrap",
        flexDirection:'row', 
        alignItems:'center', 
        paddingHorizontal:15, 
        paddingVertical:10, 
        marginVertical: 5,
        borderRadius:8,
        backgroundColor: colors[backgroundColorVariant ? backgroundColorVariant : "surface"], 
        justifyContent:"space-between",
        boxShadow: "0px -2px 8px -5px rgba(0, 0, 0, 0.15)",
        borderBottomLeftRadius:5, 
        borderBottomRightRadius:5,
        borderColor:selected ? colors[selectedColorDefault] : colors.surface
    }),
    checkAllInButton: {
        height: 40,
        width: 40,
        alignSelf: 'center', 
        backgroundColor: colors.background, 
        borderRadius: 8, 
        justifyContent: "center",
        alignItems: "center",
        marginLeft: 8,
        flexShrink: 0,
    },
    nameContainer: {
        paddingLeft: 15,
    },
    noFamilyContainer: {
        paddingLeft: 15,
        flex: 1,
        justifyContent: "center",
    },
    avatarScrollContent: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingRight: 8,
    },
    avatarItem: {
        position: "relative",
    },
    avatarBadge: {
        position: "absolute",
        bottom: -3,
        right: -3,
    },
    content: {
        flexDirection:'row', 
        alignItems:"center"
    },
    buttonContainer: (isLandscape) => ({
        flexDirection:"row", 
        gap:5, 
        alignItems:"center", 
        justifyContent:"center", 
    }),
    separator: {
        width:1, 
        height:"100%", 
        backgroundColor:colors.background, 
        marginHorizontal:10
    }
}));

export default React.memo(ProfileCard);